import {
  useGetLocale,
  useTranslate,
  useLogin,
  useGo,
  useIsAuthenticated,
  type HttpError,
} from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { Flex, Form, Alert, Checkbox, Divider, Typography } from "antd";
import { usePasswordReveal } from "./utils/usePasswordReveal";
import { createEmailRule } from "./utils/emailRule";
import { LockIcon } from "@phosphor-icons/react";
import { SocialLogin } from "./components/SocialLogin";
import { DocustreamMergeBanner } from "./components/DocustreamMergeBanner";
import { API_URL, FeatureFlags } from "@/constants";
import { useFeatureFlag } from "hooks/useFeature";
import {
  getPrivacyPolicyLinks,
  getTermsOfServiceLinks,
  isValidLang,
} from "helpers/legal-links";
import { AnalyticsEvents } from "types/analytics";
import { useAnalytics } from "hooks/useAnalytics";
import { detectNetworkError, isNetworkError } from "utils/network";
import type { NetworkErrorType } from "utils/network";
import { ScalekitLogin } from "./components/ScalekitLogin";
import { isInAppBrowser } from "helpers/webview";
import { EmailPasswordAccountModal } from "./components/EmailPasswordAccountModal";
import { AccountLockedModal } from "./components/AccountLockedModal";
import { InAppBrowserBanner } from "./components/InAppBrowserBanner";
import { useBoundStore } from "@/store";
import { AuthPage, AuthRightPanel } from "./components/AuthLayout";
import { CoverflowCarousel } from "./components/CoverflowCarousel";
import { AuthFormPanel } from "./components/AuthFormPanel";
import {
  AuthInput,
  AuthSubmitButton,
  AuthLink,
  AuthLegalText,
  AuthBottomLine,
  AuthSecurityNote,
  PasswordReveal,
} from "./components/AuthFormElements";

type LoginFormValues = {
  email: string;
  password: string;
  remember?: boolean;
};

export const LoginPage: React.FC = () => {
  const t = useTranslate();
  const go = useGo();
  const [networkError, setNetworkError] = useState<NetworkErrorType | null>(
    null
  );
  const { mutate: login, status } = useLogin({
    mutationOptions: {
      networkMode: "always",
      onError: (error: Error | HttpError) => {
        if (isNetworkError(error)) {
          void detectNetworkError(API_URL || undefined).then(setNetworkError);
          trackEvent(AnalyticsEvents.LoginFailed, {
            login_method: "email",
            reason: "network_error",
          });
        }
      },
      onMutate: () => {
        setNetworkError(null);
      },
    },
    errorNotification: (error: Error | HttpError) => {
      if (isNetworkError(error)) {
        return false;
      }
      return { type: "error", message: error.message };
    },
  } as unknown as Parameters<typeof useLogin>[0]);
  const { data: authenticated, refetch: refetchAuth } = useIsAuthenticated();
  const queryClient = useQueryClient();
  const setSidebarCollapsed = useBoundStore(
    (state) => state.setSidebarCollapsed
  );

  useEffect(() => {
    refetchAuth().catch((error) => {
      console.error("Failed to refresh auth state after login:", error);
      Sentry.captureException(error);
    });
  }, [refetchAuth]);

  const isAuthenticated = Boolean(authenticated?.authenticated);
  const ready = authenticated !== undefined && !isAuthenticated;

  useEffect(() => {
    if (status === "success") {
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["getUserIdentity"] }),
        refetchAuth(),
      ]).catch((error) => {
        console.error("Failed to refresh auth state after login:", error);
        Sentry.captureException(error);
      });
    }
  }, [status, refetchAuth, queryClient]);

  const [form] = Form.useForm();
  const isLoading = status === "loading";
  const isLibertifySocialAuthEnabled = useFeatureFlag(
    FeatureFlags.LibertifySocialAuth
  );
  const isScaleKitAuthEnabled = useFeatureFlag(FeatureFlags.ScaleKitAuth);
  const isSocialAuthEnabled = isLibertifySocialAuthEnabled;
  const locale = useGetLocale();
  const lang = locale()?.split("-")[0];
  const termsOfServiceLink =
    getTermsOfServiceLinks()[isValidLang(lang) ? lang : "en"];
  const privacyPolicyLink =
    getPrivacyPolicyLinks()[isValidLang(lang) ? lang : "en"];
  const { trackEvent } = useAnalytics();
  const loginAttemptCount = useRef(0);

  const location = useLocation();
  const locationState = location.state as {
    oauth_error?: string;
    provider?: string;
  } | null;
  const oauthError = locationState?.oauth_error;
  const oauthProvider = locationState?.provider;

  const searchParams = new URLSearchParams(document.location.search);

  let email: string;
  try {
    email = atob(searchParams.get("email") ?? "");
  } catch (error) {
    console.error("Error decoding email", error);
    email = "";
  }

  const [showEmailAccountModal, setShowEmailAccountModal] = useState(
    oauthError === "email_account_exists"
  );
  const [showAccountLockedModal, setShowAccountLockedModal] = useState(
    oauthError === "account_locked"
  );

  const redirectToSignup = () => {
    go({ to: "/register" });
  };

  const onFinish = (values: LoginFormValues) => {
    loginAttemptCount.current += 1;
    trackEvent(AnalyticsEvents.LoginAttempt, {
      login_method: "email",
      attempt_count: loginAttemptCount.current,
    });
    login(values);
  };

  const hasResetContext = useRef(false);

  useLayoutEffect(() => {
    if (isAuthenticated) {
      hasResetContext.current = false;
      go({ to: "/" });
    } else if (ready && !hasResetContext.current) {
      hasResetContext.current = true;
    }
  }, [isAuthenticated, ready, go]);

  useEffect(() => {
    setSidebarCollapsed(true);
    return () => {
      setSidebarCollapsed(false);
    };
  }, [setSidebarCollapsed]);

  const { isPasswordVisible, handleFieldFocus, handleFieldBlur } =
    usePasswordReveal(form);

  if (!ready) return null;

  return (
    <AuthPage>
      <CoverflowCarousel />

      <AuthRightPanel>
        <DocustreamMergeBanner />
        <AuthFormPanel title={t("pages.login.signin")}>
          {isInAppBrowser() ? (
            <InAppBrowserBanner />
          ) : (
            <>
              {isSocialAuthEnabled && <SocialLogin action="login" />}
              {isScaleKitAuthEnabled && <ScalekitLogin />}
              <Divider style={{ width: "100%", margin: 0 }}>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 13, color: "#9ca3af" }}
                >
                  {t("pages.login.or")}
                </Typography.Text>
              </Divider>

              <LoginFormRG
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ email, remember: true }}
                requiredMark={false}
              >
                <Form.Item
                  name="email"
                  label={false}
                  validateTrigger="onBlur"
                  rules={[createEmailRule(t("pages.login.errors.validEmail"))]}
                  style={{ margin: 0, width: "100%" }}
                >
                  <AuthInput
                    type="email"
                    placeholder={t("pages.login.fields.email")}
                    autoComplete="email"
                    aria-label="Email address"
                    onFocus={handleFieldFocus}
                    onBlur={handleFieldBlur}
                  />
                </Form.Item>

                <PasswordReveal $visible={isPasswordVisible}>
                  <div>
                    <Form.Item
                      name="password"
                      label={false}
                      validateTrigger={[]}
                      rules={[
                        {
                          validator: (_: unknown, value: string) => {
                            if (!value)
                              return Promise.reject(
                                new Error(
                                  t("pages.login.errors.requiredPassword")
                                )
                              );
                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ margin: 0, width: "100%" }}
                    >
                      <AuthInput
                        type="password"
                        placeholder={t("pages.login.fields.password")}
                        autoComplete="current-password"
                        aria-label="Password"
                        onFocus={handleFieldFocus}
                        onBlur={handleFieldBlur}
                      />
                    </Form.Item>

                    {networkError === "vpn" && (
                      <Alert
                        type="error"
                        message={t("pages.login.errors.networkBlocked")}
                      />
                    )}

                    {networkError === "offline" && (
                      <Alert
                        type="warning"
                        message={t("pages.login.errors.offline")}
                      />
                    )}

                    {networkError === "api_error" && (
                      <Alert
                        type="warning"
                        message={t("pages.login.errors.apiUnreachable")}
                      />
                    )}

                    <Flex
                      justify="space-between"
                      align="center"
                      style={{ width: "100%" }}
                    >
                      <Form.Item
                        name="remember"
                        valuePropName="checked"
                        style={{ margin: 0 }}
                      >
                        <Checkbox aria-label="Remember me">
                          {t("pages.login.buttons.rememberMe")}
                        </Checkbox>
                      </Form.Item>
                      <AuthLink href="/forgot-password">
                        {t("pages.login.buttons.forgotPassword")}
                      </AuthLink>
                    </Flex>
                  </div>
                </PasswordReveal>

                <Form.Item style={{ margin: 0, width: "100%" }}>
                  <AuthSubmitButton type="submit" disabled={isLoading}>
                    {isLoading ? "..." : t("pages.login.signin")}
                  </AuthSubmitButton>
                </Form.Item>
              </LoginFormRG>

              <AuthBottomLine>
                {t("pages.login.buttons.noAccount")}{" "}
                <AuthLink onClick={redirectToSignup}>
                  {t("pages.login.signup")}
                </AuthLink>
              </AuthBottomLine>

              <AuthLegalText>
                <AuthLink href={termsOfServiceLink} target="_blank">
                  {t("pages.auth.register.termsOfService")}
                </AuthLink>
                {" & "}
                <AuthLink href={privacyPolicyLink} target="_blank">
                  {t("pages.auth.register.privacyPolicy")}
                </AuthLink>
              </AuthLegalText>

              <AuthSecurityNote>
                <LockIcon size={12} />
                {t("pages.auth.register.securityNote")}
              </AuthSecurityNote>
            </>
          )}
        </AuthFormPanel>
      </AuthRightPanel>

      <EmailPasswordAccountModal
        open={showEmailAccountModal}
        provider={oauthProvider}
        onGoToLogin={() => setShowEmailAccountModal(false)}
        onClose={() => setShowEmailAccountModal(false)}
      />
      <AccountLockedModal
        open={showAccountLockedModal}
        onClose={() => setShowAccountLockedModal(false)}
      />
    </AuthPage>
  );
};

// ─── Login-specific styled components ────────────────────────────────────────

const LoginFormRG = styled(Form<LoginFormValues>)`
  &.ant-form {
    display: contents;
  }

  .ant-form-item-explain-error {
    font-size: 12px;
  }
`;
